import React, { Component } from 'react';
import { Button, RichTextInput, BlockUi } from 'app/components/Basics/';
import { Loading } from '@ecosystem/ui-lib/core/components/Loading';
import { ShowError, ShowSuccess } from '../../../components/toast';
import StaticTextService from '../../../services/staticText.service';
import GetErrorMessage from '../../../helpers/error-message';
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import './StaticTextsComponent.scss';

class StaticTexts extends Component {
    constructor(props) {
        super(props);

        this.tableScrollRef = React.createRef();
        this.popupScrollRef = React.createRef();

        const languages = props.languages || [];
        const defaultLang = languages.find(l => l.isDefault) || languages[0] || null;

        this.state = {
            selectedLanguageCode: defaultLang ? defaultLang.code : '',
            rows: [],
            originalRows: [],
            loading: false,
            uiBlocked: false,
            errorMessage: null,
            successMessage: null,
            tableVersion: 0,
            showHidden: false,
            translationPopupLabel: null,
            translationRows: [],
            originalTranslationRows: [],
            translationLoading: false,
        };
    }

    componentDidMount() {
        if (this.state.selectedLanguageCode) {
            this.loadData(this.state.selectedLanguageCode, this.state.showHidden);
        }
    }

    componentDidUpdate(prevProps, prevState) {
        if (prevState.tableVersion !== this.state.tableVersion) {
            this.resetTableScrollTop();
        }
    }

    resetTableScrollTop() {
        // Delay until after Lexical editors finish async initialisation
        setTimeout(() => {
            if (this.tableScrollRef.current) this.tableScrollRef.current.scrollTop = 0;
        }, 0);
    }

    loadData(languageCode, showHidden = false) {
        this.setState({ loading: true, rows: [], originalRows: [] });
        StaticTextService.find(languageCode, showHidden)
            .then(response => {
                const rows = response.data;
                this.setState({ rows: cloneDeep(rows), originalRows: cloneDeep(rows), loading: false });
                this.resetTableScrollTop();
            })
            .catch(error => {
                this.setState({ loading: false, errorMessage: GetErrorMessage(error) });
            });
    }

    onLanguageChange(e) {
        const code = e.target.value;
        this.setState({ selectedLanguageCode: code }, () => this.loadData(code, this.state.showHidden));
    }

    onToggleShowHidden() {
        const newShowHidden = !this.state.showHidden;
        this.setState({ showHidden: newShowHidden }, () => this.loadData(this.state.selectedLanguageCode, newShowHidden));
    }

    onTextChange(id, label, newText, isHtmlEdited) {
        const rows = this.state.rows.map(r =>
            (id !== 0 ? r.id === id : r.label === label && r.id === 0)
                ? { ...r, text: newText, isHtmlEdited: !!isHtmlEdited }
                : r
        );
        this.setState({ rows });
    }

    onDescriptionChange(id, label, e) {
        const value = e.target.value;
        const rows = this.state.rows.map(r =>
            (id !== 0 ? r.id === id : r.label === label) ? { ...r, description: value } : r
        );
        this.setState({ rows });
    }

    onHideChange(label, checked) {
        const rows = this.state.rows.map(r => r.label === label ? { ...r, isHideOnAqua: checked } : r);
        this.setState({ rows });
    }

    getDirtyRows() {
        return this.state.rows.filter(row => {
            const original = this.state.originalRows.find(o =>
                row.id !== 0 ? o.id === row.id : o.label === row.label && o.languageCode === row.languageCode
            );
            return original && !isEqual(row, original);
        });
    }

    saveAll() {
        const dirty = this.getDirtyRows();
        if (!dirty.length) return;

        this.setState({ uiBlocked: true });
        const canAdmin = this.props.userPermissions?.CanAdminStaticText;

        const updateRows = dirty.filter(row => row.id !== 0);
        const createRows = dirty.filter(row => row.id === 0 && row.text && row.text.trim());

        const textUpdates = updateRows.map(row =>
            StaticTextService.updateText(row.id, row.text, row.isHtmlEdited)
        );

        const translationCreates = createRows.map(row =>
            StaticTextService.createTranslation(row.label, row.languageCode, row.text, row.isHtmlEdited)
        );
        const adminUpdates = [];
        if (canAdmin) {
            const processedLabels = new Set();
            dirty.forEach(row => {
                if (!processedLabels.has(row.label)) {
                    const orig = this.state.originalRows.find(o =>
                        row.id !== 0 ? o.id === row.id : o.label === row.label && o.languageCode === row.languageCode
                    );
                    if (orig && (row.description !== orig.description || row.isHideOnAqua !== orig.isHideOnAqua)) {
                        adminUpdates.push(
                            StaticTextService.updateAdmin(row.label, row.description, row.isHideOnAqua)
                        );
                        processedLabels.add(row.label);
                    }
                }
            });
        }

        Promise.all([...textUpdates, ...translationCreates, ...adminUpdates])
            .then(() => {
                // Re-fetch so new translations get real IDs from backend
                const { selectedLanguageCode, showHidden } = this.state;
                return StaticTextService.find(selectedLanguageCode, showHidden)
                    .then(response => {
                        const refreshed = response.data;
                        this.setState(prevState => ({
                            rows: cloneDeep(refreshed),
                            originalRows: cloneDeep(refreshed),
                            uiBlocked: false,
                            successMessage: 'Changes saved successfully.',
                            tableVersion: prevState.tableVersion + 1,
                        }));
                    });
            })
            .catch(error => {
                this.setState({ uiBlocked: false, errorMessage: GetErrorMessage(error) });
            });
    }

    // ── Translation popup ──────────────────────────────────────────────────

    openTranslationPopup(label) {
        this.setState({ translationPopupLabel: label, translationLoading: true, translationRows: [], originalTranslationRows: [] });
        StaticTextService.getTranslations(label)
            .then(response => {
                const rows = response.data;
                this.setState({ translationRows: cloneDeep(rows), originalTranslationRows: cloneDeep(rows), translationLoading: false }, () => {
                    if (this.popupScrollRef.current) this.popupScrollRef.current.scrollTop = 0;
                });
            })
            .catch(error => {
                this.setState({ translationLoading: false, errorMessage: GetErrorMessage(error) });
            });
    }

    closeTranslationPopup() {
        this.setState({ translationPopupLabel: null, translationRows: [], originalTranslationRows: [] });
    }

    onTranslationTextChange(languageCode, newText, isHtmlEdited) {
        const translationRows = this.state.translationRows.map(r =>
            r.languageCode === languageCode ? { ...r, text: newText, isHtmlEdited: !!isHtmlEdited } : r
        );
        this.setState({ translationRows });
    }

    saveTranslations() {
        const dirty = this.state.translationRows.filter(row => {
            const orig = this.state.originalTranslationRows.find(o => o.languageCode === row.languageCode);
            return orig && row.text !== orig.text;
        });
        if (!dirty.length) {
            this.closeTranslationPopup();
            return;
        }

        this.setState({ uiBlocked: true });

        // Rows with a real DB id: update in place
        const updateRows = dirty.filter(row => row.id !== 0);
        // Rows that don't exist yet (id=0) but now have text: create new records
        const createRows = dirty.filter(row => row.id === 0 && row.text && row.text.trim());

        const updates = updateRows.map(row => StaticTextService.updateText(row.id, row.text, row.isHtmlEdited));
        const creates = createRows.map(row =>
            StaticTextService.createTranslation(row.label, row.languageCode, row.text, row.isHtmlEdited)
        );

        Promise.all([...updates, ...creates])
            .then(() => {
                this.closeTranslationPopup();
                const { selectedLanguageCode, showHidden } = this.state;
                return StaticTextService.find(selectedLanguageCode, showHidden)
                    .then(response => {
                        const refreshed = response.data;
                        this.setState(prevState => ({
                            rows: cloneDeep(refreshed),
                            originalRows: cloneDeep(refreshed),
                            uiBlocked: false,
                            successMessage: 'Translations saved successfully.',
                            tableVersion: prevState.tableVersion + 1,
                        }));
                    });
            })
            .catch(error => {
                this.setState({ uiBlocked: false, errorMessage: GetErrorMessage(error) });
            });
    }

    // ── Render helpers ─────────────────────────────────────────────────────

    renderTranslationPopup() {
        const { translationPopupLabel, translationRows, translationLoading } = this.state;
        if (!translationPopupLabel) return null;

        const canEdit = this.props.userPermissions?.CanChangeStaticText || this.props.userPermissions?.CanAdminStaticText;

        return (
            <div className="tp-overlay" onClick={(e) => { if (e.target === e.currentTarget) this.closeTranslationPopup(); }}>
                <div className="tp-dialog">

                    <div className="tp-header">
                        <h3>Translations for: <code>{translationPopupLabel}</code></h3>
                    </div>

                    <div className="tp-body" ref={this.popupScrollRef}>
                        {translationLoading
                            ? <Loading active />
                            : (
                                <table className="tp-table">
                                    <thead>
                                        <tr>
                                            <th className="tp-col-lang">Language</th>
                                            <th>Translation</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {translationRows.map(row => (
                                            <tr key={row.languageCode}>
                                                <td className="tp-col-lang">{row.languageDescription || row.languageCode}</td>
                                                <td>
                                                    <RichTextInput
                                                        key={`trans-${row.languageCode}`}
                                                        id={`trans-${row.languageCode}`}
                                                        value={row.text}
                                                        disabled={!canEdit}
                                                        maxLines={20000}
                                                        editorLines={5}
                                                        ctrlsPosition="right"
                                                        allowRawHtmlEdit
                                                        isHtmlEdited={row.isHtmlEdited}
                                                        onValueChange={(val, _id, htmlEdited) => this.onTranslationTextChange(row.languageCode, val, htmlEdited)}
                                                    />
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )
                        }
                    </div>

                    <div className="tp-footer">
                        {canEdit && (
                            <Button small onClick={() => this.saveTranslations()}>Save</Button>
                        )}
                        <Button small onClick={() => this.closeTranslationPopup()}>Close</Button>
                    </div>

                </div>
            </div>
        );
    }

    render() {
        const { rows, loading, uiBlocked, selectedLanguageCode, errorMessage, successMessage, tableVersion, showHidden } = this.state;
        const languages = this.props.languages || [];
        const isDefaultLanguage = languages.some(l => l.isDefault && l.code === selectedLanguageCode);
        const userPermissions = this.props.userPermissions || {};
        const canEdit = userPermissions.CanChangeStaticText || userPermissions.CanAdminStaticText;
        const canAdmin = userPermissions.CanAdminStaticText;
        const hasDirty = this.getDirtyRows().length > 0;

        return (
            <BlockUi tag="div" blocking={uiBlocked}>
                <div className="static-texts-page">
                    <div className="static-texts-page-inner">

                        <div className="static-texts-action-bar">
                            <div className="action-bar-left">
                                <label htmlFor="lang-select">Language:</label>
                                <select
                                    id="lang-select"
                                    value={selectedLanguageCode}
                                    onChange={this.onLanguageChange.bind(this)}
                                    className="lang-dropdown"
                                >
                                    {languages.map(lang => (
                                        <option key={lang.code} value={lang.code}>
                                            {lang.description}
                                        </option>
                                    ))}
                                </select>
                                {canAdmin && (
                                    <button
                                        className="toggle-hidden-btn"
                                        onClick={() => this.onToggleShowHidden()}
                                        type="button"
                                    >
                                        {showHidden ? 'Hide Hidden' : 'Show Hidden'}
                                    </button>
                                )}
                            </div>
                            <div className="action-bar-right">
                                {canEdit && (
                                    <Button
                                        small
                                        disabled={!hasDirty}
                                        onClick={() => this.saveAll()}
                                    >
                                        Save Changes
                                    </Button>
                                )}
                            </div>
                        </div>

                        <div className="static-texts-card">
                            {loading && <Loading active />}
                            {!loading && rows.length === 0 && (
                                <div className="no-data">No static texts found for this language.</div>
                            )}
                            {!loading && rows.length > 0 && (
                                <div className="static-texts-table-scroll" ref={this.tableScrollRef}>
                                <table className="static-texts-table">
                                    <thead>
                                        <tr>
                                            {canAdmin && <th className="col-hide">Hide</th>}
                                            <th className="col-label">Label</th>
                                            <th className="col-description">Description</th>
                                            <th className="col-text">Text</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {rows.map(row => (
                                            <tr key={`${row.label}-${row.languageCode}`} className={[row.isHideOnAqua ? 'row-hidden' : '', !row.translationExists ? 'row-missing-translation' : ''].filter(Boolean).join(' ')}>
                                                {canAdmin && (
                                                    <td className="col-hide">
                                                        <input
                                                            type="checkbox"
                                                            checked={!!row.isHideOnAqua}
                                                            onChange={e => this.onHideChange(row.label, e.target.checked)}
                                                            title="Hide from non-admin users"
                                                        />
                                                    </td>
                                                )}
                                                <td className="col-label">
                                                    <button
                                                        className="label-link"
                                                        onClick={() => this.openTranslationPopup(row.label)}
                                                        title="View / edit all translations"
                                                    >
                                                        {row.label}
                                                    </button>
                                                </td>
                                                <td className="col-description">
                                                    {canAdmin
                                                        ? (
                                                            <textarea
                                                                value={row.description || ''}
                                                                onChange={e => this.onDescriptionChange(row.id, row.label, e)}
                                                                className="description-input"
                                                                rows={2}
                                                            />
                                                        )
                                                        : <span className="description-readonly">{row.description}</span>
                                                    }
                                                </td>
                                                <td className="col-text">
                                                    <div className="text-cell-wrapper">
                                                        <RichTextInput
                                                            key={`${row.label}-${selectedLanguageCode}-${tableVersion}`}
                                                            id={`st-${row.id}-${row.label}`}
                                                            value={row.text}
                                                            disabled={!canEdit}
                                                            maxLines={20000}
                                                            editorLines={4}
                                                            ctrlsPosition="right"
                                                            allowRawHtmlEdit
                                                            isHtmlEdited={row.isHtmlEdited}
                                                            onValueChange={(val, _id, htmlEdited) => this.onTextChange(row.id, row.label, val, htmlEdited)}
                                                        />
                                                    </div>
                                                    {!row.translationExists && !isDefaultLanguage && (
                                                        <div className="translation-missing-hint">
                                                            Translation does not exist. Please add a translation, referring to the default value from{' '}
                                                            <button
                                                                className="hint-info-icon"
                                                                title={row.defaultText}
                                                                aria-label="Show default text"
                                                                type="button"
                                                            >
                                                                &#x2139;
                                                            </button>.
                                                        </div>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                </div>
                            )}
                        </div>

                    </div>
                </div>

                {this.renderTranslationPopup()}

                {successMessage && (
                    <ShowSuccess
                        clearMessage={() => this.setState({ successMessage: null })}
                        message={successMessage}
                    />
                )}
                {errorMessage && (
                    <ShowError
                        clearErrorMessage={() => this.setState({ errorMessage: null })}
                        errorMessage={errorMessage}
                    />
                )}
            </BlockUi>
        );
    }
}

export default StaticTexts;
